import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
} from '@mui/material';
import subscriptionService from '../../../../services/SubscriptionService';
import CheckoutService from '../../../../services/CheckoutService';
import { SubscriptionStatus, StripeProduct, StripeProductPrice } from '../../../../types/Subscription';

interface SubscriptionManagementPanelProps {
  organizationId: string;
}

/**
 * Shows subscription details (credits used/remaining) and available subscription plans.
 * Allows users to select a plan and start the checkout process.
 */
const SubscriptionManagementPanel: React.FC<SubscriptionManagementPanelProps> = ({
  organizationId,
}) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [availableProducts, setAvailableProducts] = useState<StripeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Initialize checkout service
  const checkoutService = new CheckoutService();

  useEffect(() => {
    const load = async () => {
      try {
        // Fetch subscription status
        const status = await subscriptionService.getSubscriptionStatus(organizationId);
        setSubscriptionStatus(status);

        // Fetch available products
        const products = await subscriptionService.getAvailableSubscriptions(organizationId);
        setAvailableProducts(products);
      } catch (e) {
        console.error(e);
        setError('Unable to load subscription information.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [organizationId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error alert but continue displaying the panel with default values
  const showError = error !== null;

  // Use default values if subscription status is not available
  const defaultStatus: SubscriptionStatus = {
    organization_id: organizationId,
    seat_credits: 0
  };

  const currentStatus = subscriptionStatus || defaultStatus;

  // Handle product selection
  const handleProductSelect = (productId: string) => {
    setSelectedProduct(productId);
    setSelectedPrice(null); // Reset price selection when product changes
  };

  // Handle price selection
  const handlePriceSelect = (priceId: string) => {
    setSelectedPrice(priceId);
  };

  // Start checkout process
  const handleCheckout = async (productId?: string, priceId?: string) => {
    const finalProductId = productId || selectedProduct;
    const finalPriceId = priceId || selectedPrice;

    if (!finalProductId || !finalPriceId) return;

    setCheckoutLoading(true);
    try {
      const checkoutUrl = await createCheckoutSession(organizationId, finalProductId, finalPriceId);

      // Redirect to Stripe checkout
      window.location.href = checkoutUrl;
    } catch (e) {
      console.error('Failed to start checkout:', e);
      setError('Unable to start checkout process. Please try again later.');
      setCheckoutLoading(false);
    }
  };

  // Create checkout session using CheckoutService
  const createCheckoutSession = async (organizationId: string, productId: string, priceId: string): Promise<string> => {
    try {
      // Generate proper success and cancel URLs - redirect back to current page
      const baseUrl = window.location.origin;
      const currentPath = window.location.pathname;
      const successUrl = `${baseUrl}/subscriptions/success?organization_id=${organizationId}&session_id={CHECKOUT_SESSION_ID}&return_to=${encodeURIComponent(currentPath)}`;
      const cancelUrl = `${baseUrl}/subscriptions/cancel?organization_id=${organizationId}`;

      // Create subscription checkout session
      const response = await checkoutService.createSubscriptionCheckout(
        organizationId,
        productId, // planId - use the passed product ID as the plan ID
        priceId,
        successUrl,
        cancelUrl,
        {
          product_id: productId,
          organization_id: organizationId
        }
      );

      return response.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw new Error('Failed to create checkout session');
    }
  };

  return (
    <Box sx={{ my: 2 }}>
      {showError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Unable to load subscription information.'}
        </Alert>
      )}

      {/* Current Subscription Info */}
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Current Subscription
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="body1">
              Applicant credits: <strong>{currentStatus.seat_credits}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Subscription Status
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Subscription Plans */}
      {availableProducts && availableProducts.length > 0 && (
        <>
          {/* Subscription Plans Section */}
          <Box sx={{ mb: 6 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Subscription Plans
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Choose the perfect plan for your mentoring program
              </Typography>
            </Box>

            {/* Subscription Plans */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {availableProducts
                .filter((product: StripeProduct) =>
                  product.product_active &&
                  product.prices.some(price => price.interval) // Only products with recurring intervals
                )
                .sort((a, b) => {
                  const aPrice = a.prices?.[0]?.amount || 0;
                  const bPrice = b.prices?.[0]?.amount || 0;
                  return aPrice - bPrice;
                })
                .map((product: StripeProduct, index: number, array: StripeProduct[]) => {
                  // Mark the middle plan as popular, or if there's an even number, prefer the higher middle
                  const midIndex = Math.floor(array.length / 2);
                  const isPopular = index === midIndex;
                  const isFree = product.prices.some(price => price.amount === 0);
                  return (
                    <Grid item xs={12} md={6} lg={4} key={product.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: '100%',
                          position: 'relative',
                          borderColor: selectedProduct === product.id ? 'primary.main' : (isPopular && !isFree ? 'primary.main' : (isFree ? 'success.main' : 'divider')),
                          borderWidth: (isPopular && !isFree) || isFree ? 2 : 1,
                          boxShadow: selectedProduct === product.id ? '0 8px 24px rgba(25, 118, 210, 0.2)' : ((isPopular && !isFree) ? '0 4px 16px rgba(25, 118, 210, 0.1)' : 'none'),
                          cursor: isFree ? 'default' : 'pointer',
                          transition: 'all 0.3s ease-in-out',
                          transform: selectedProduct === product.id ? 'translateY(-4px)' : 'none',
                          '&:hover': isFree ? {} : {
                            borderColor: 'primary.main',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                        onClick={() => !isFree && handleProductSelect(product.id)}
                      >
                        {isPopular && !isFree && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -1,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              bgcolor: 'primary.main',
                              color: 'white',
                              px: 2,
                              py: 0.5,
                              borderRadius: '0 0 8px 8px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          >
                            Most Popular
                          </Box>
                        )}

                        <CardContent sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mt: (isPopular && !isFree) ? 2 : 0 }}>
                            {product.name}
                          </Typography>

                          {product.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: '3em' }}>
                              {product.description}
                            </Typography>
                          )}

                          {/* Price Display */}
                          {product.prices && product.prices.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              {product.prices.map((price: StripeProductPrice, index: number) => {
                                const costPerCredit = product.seat_credits > 0 ? (price.amount / 100) / product.seat_credits : 0;
                                return (
                                  <Box key={price.id} sx={{ mb: index < product.prices.length - 1 ? 1 : 0 }}>
                                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: isFree ? 'success.main' : 'primary.main' }}>
                                      {price.amount === 0 ? 'FREE' : `$${(price.amount / 100).toFixed(0)}`}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                      {price.currency.toUpperCase()}
                                      {price.interval && ` / ${price.interval_count} ${price.interval}`}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                      {product.seat_credits} seat credits
                                    </Typography>
                                    {price.amount > 0 && (
                                      <Typography variant="body2" color="text.secondary">
                                        ${costPerCredit.toFixed(2)} per credit
                                      </Typography>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}

                          {isFree ? (
                            <Box
                              sx={{
                                py: 2,
                                px: 3,
                                backgroundColor: 'success.light',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'success.main'
                              }}
                            >
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                                ✓ Automatically Included
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Every organization gets these credits each quarter
                              </Typography>
                            </Box>
                          ) : (
                            <Button
                              variant={isPopular ? 'contained' : 'outlined'}
                              size="large"
                              fullWidth
                              disabled={checkoutLoading}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleProductSelect(product.id);
                                if (product.prices && product.prices.length > 0) {
                                  handlePriceSelect(product.prices[0].id);
                                  // Pass values directly to avoid async state issues
                                  handleCheckout(product.id, product.prices[0].id);
                                }
                              }}
                              sx={{
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                textTransform: 'none'
                              }}
                            >
                              {checkoutLoading && selectedProduct === product.id ? 'Processing...' : 'Choose Plan'}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid>
          </Box>

          {/* A La Carte Section */}
          <Box sx={{ mb: 6 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                A La Carte Options
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
                Flexible credit packs for your specific needs
              </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {/* A La Carte Products (one-time purchases) */}
              {availableProducts
                .filter((product: StripeProduct) =>
                  product.product_active &&
                  !product.prices.some(price => price.interval) // Products without recurring intervals
                )
                .map((product: StripeProduct) => (
                  <Grid item xs={12} md={6} lg={4} key={product.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderColor: selectedProduct === product.id ? 'primary.main' : 'divider',
                        boxShadow: selectedProduct === product.id ? '0 8px 24px rgba(25, 118, 210, 0.2)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                        }
                      }}
                      onClick={() => handleProductSelect(product.id)}
                    >
                      <CardContent sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {product.name}
                        </Typography>

                        {product.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {product.description}
                          </Typography>
                        )}

                        {/* Price Display */}
                        {product.prices && product.prices.length > 0 && (
                          <Box sx={{ mb: 3 }}>
                            {product.prices.map((price: StripeProductPrice) => {
                              const costPerCredit = product.seat_credits > 0 ? (price.amount / 100) / product.seat_credits : (price.amount / 100);
                              return (
                                <Box key={price.id}>
                                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                                    ${(price.amount / 100).toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    {product.seat_credits > 0 ? `${product.seat_credits} credits` : 'per credit'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    ${costPerCredit.toFixed(2)} per credit
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Box>
                        )}

                        <Button
                          variant="outlined"
                          size="large"
                          fullWidth
                          disabled={checkoutLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProductSelect(product.id);
                            if (product.prices && product.prices.length > 0) {
                              handlePriceSelect(product.prices[0].id);
                              handleCheckout();
                            }
                          }}
                          sx={{
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            borderColor: 'secondary.main',
                            color: 'secondary.main',
                            '&:hover': {
                              borderColor: 'secondary.dark',
                              backgroundColor: 'secondary.light'
                            }
                          }}
                        >
                          {checkoutLoading && selectedProduct === product.id ? 'Processing...' : 'Buy Credits'}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
};

export default SubscriptionManagementPanel;
